import type { ComputedBadge } from "../../shared/badges";
import { earnedBadges, inProgressBadges } from "../../shared/badges";

// The gamification shelf: a wall of earned medallions plus a "closest next"
// rail so there's always an obvious thing to chase. Progress is a real ring,
// not a number — the arc reads at a glance on a phone.

function Ring({
  pct,
  color,
  size = 64,
  stroke = 5,
  earned,
  icon,
}: {
  pct: number;
  color: string;
  size?: number;
  stroke?: number;
  earned: boolean;
  icon: string;
}) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (Math.max(0, Math.min(100, pct)) / 100) * c;
  return (
    <div
      className="relative grid place-items-center"
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="currentColor"
          strokeWidth={stroke}
          className="text-ink/10"
        />
        {pct > 0 && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={color}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${dash} ${c}`}
          />
        )}
      </svg>
      <span
        className={`absolute text-2xl leading-none transition ${
          earned ? "" : "opacity-45 grayscale"
        }`}
        aria-hidden
      >
        {icon}
      </span>
      {earned && (
        <span
          className="absolute -bottom-0.5 -right-0.5 grid h-5 w-5 place-items-center rounded-full text-[10px] font-bold text-white shadow"
          style={{ background: color }}
          aria-hidden
        >
          ✓
        </span>
      )}
    </div>
  );
}

function Medallion({ badge }: { badge: ComputedBadge }) {
  return (
    <div className="flex w-[84px] shrink-0 flex-col items-center gap-1 text-center">
      <Ring
        pct={badge.earned ? 100 : badge.pct}
        color={badge.color}
        earned={badge.earned}
        icon={badge.icon}
      />
      <div className="mt-0.5 w-full truncate text-[12px] font-semibold text-ink">
        {badge.title}
      </div>
      <div className="w-full truncate text-[10.5px] text-ink/55 tabular-nums">
        {badge.earned ? "Earned" : badge.caption}
      </div>
    </div>
  );
}

// Wide progress row for the "in progress" rail — ring + label + bar so the
// gap-to-next reads clearly.
function ProgressRow({ badge }: { badge: ComputedBadge }) {
  return (
    <div className="flex items-center gap-3">
      <Ring
        pct={badge.pct}
        color={badge.color}
        size={44}
        stroke={4}
        earned={false}
        icon={badge.icon}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="truncate text-sm font-semibold text-ink">
            {badge.title}
          </span>
          <span className="shrink-0 text-xs text-ink/55 tabular-nums">
            {badge.caption}
          </span>
        </div>
        <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-ink/10">
          <div
            className="h-full rounded-full"
            style={{ width: `${badge.pct}%`, background: badge.color }}
          />
        </div>
      </div>
    </div>
  );
}

export function BadgeShelf({ badges }: { badges: ComputedBadge[] }) {
  const earned = earnedBadges(badges);
  const inProgress = inProgressBadges(badges).slice(0, 6);

  if (earned.length === 0 && inProgress.length === 0) return null;

  return (
    <section className="card p-5">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink/60">
          Badges
        </h2>
        {earned.length > 0 && (
          <span className="text-xs text-ink/50 tabular-nums">
            {earned.length} earned
          </span>
        )}
      </div>

      {earned.length > 0 ? (
        <div className="mt-3 flex gap-3 overflow-x-auto pb-2 [-webkit-overflow-scrolling:touch]">
          {earned.map((b) => (
            <Medallion key={b.id} badge={b} />
          ))}
        </div>
      ) : (
        <p className="mt-2 text-xs text-ink/55">
          No badges yet — log a few fish and they'll start unlocking.
        </p>
      )}

      {inProgress.length > 0 && (
        <div className="mt-4 border-t border-hairline pt-4">
          <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-ink/45">
            Closest to unlock
          </h3>
          <div className="flex flex-col gap-3">
            {inProgress.map((b) => (
              <ProgressRow key={b.id} badge={b} />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

// Compact horizontal strip for the shared-trip page — earned first, then the
// closest few, as small medallions. No card chrome; caller wraps it.
export function BadgeStrip({ badges }: { badges: ComputedBadge[] }) {
  const earned = earnedBadges(badges);
  const inProgress = inProgressBadges(badges);
  const shown = [...earned, ...inProgress].slice(0, 10);
  if (shown.length === 0) return null;
  return (
    <div className="flex gap-3 overflow-x-auto pb-1 [-webkit-overflow-scrolling:touch]">
      {shown.map((b) => (
        <Medallion key={b.id} badge={b} />
      ))}
    </div>
  );
}
